/**
 * Single source of truth for the Back Her Game support experience.
 *
 *   support_experience = none | direct_only | shop_only | both
 *
 * - direct_only → existing Direct Support experience, unchanged. No chooser.
 * - shop_only   → one Back Her Game card that goes straight into Her Collection.
 * - both        → the two-option chooser (Back Her Directly / Shop Her Collection).
 * - none        → no Back Her Game pathway card (existing profile behaviour).
 *
 * Direct Support always comes from the athlete's real configuration (the
 * profile's existing directSupport readiness). Admin preview may only simulate
 * Shop availability, never Direct Support.
 */
export type SupportExperience = "none" | "direct_only" | "shop_only" | "both";
export type ShopMode = "off" | "preview" | "live";

/** Master real-collections switch. Keep OFF until Shopify is connected and approved. */
export const LIVE_COMMERCE_UI_ENABLED = false;

export type RightsStatus = string | null | undefined;
const rightsOk = (s: RightsStatus) => s === "approved" || s === "not_required";

export type CommerceSettingsLite = {
  commerce_enabled?: boolean | null;
  is_test?: boolean | null;
  shop_status?: string | null;
  athlete_rights_status?: RightsStatus;
} | null;

/** A live product as seen by the public rule. */
export type LiveProductLite = {
  approval_status?: string | null;
  display_enabled?: boolean | null;
  archived_at?: string | null;
  team_ip_permission_status?: RightsStatus;
};

/** Every launch condition must hold for a real (non-preview) collection. */
export const isShopLive = (input: {
  masterSwitch?: boolean;
  athletePublished: boolean;
  settings: CommerceSettingsLite;
  products: LiveProductLite[];
}): boolean => {
  const master = input.masterSwitch ?? LIVE_COMMERCE_UI_ENABLED;
  const s = input.settings;
  if (!master || !input.athletePublished || !s) return false;
  if (!s.commerce_enabled || s.shop_status !== "live" || s.is_test) return false;
  if (!rightsOk(s.athlete_rights_status)) return false;
  return input.products.some(
    (p) =>
      p.approval_status === "live" &&
      p.display_enabled !== false &&
      !p.archived_at &&
      rightsOk(p.team_ip_permission_status),
  );
};

/** Preview (admin override or test shop) or genuinely live; otherwise off. */
export const resolveShopMode = (input: {
  adminOverride: boolean;
  settings: CommerceSettingsLite;
  athletePublished: boolean;
  products?: LiveProductLite[];
  masterSwitch?: boolean;
}): ShopMode => {
  if (input.adminOverride) return "preview";
  const s = input.settings;
  if (s?.commerce_enabled && s.is_test && s.shop_status !== "off") return "preview";
  return isShopLive({ ...input, products: input.products ?? [] }) ? "live" : "off";
};

export const resolveSupportExperience = (directSupport: boolean, shopAvailable: boolean): SupportExperience =>
  directSupport && shopAvailable ? "both" : directSupport ? "direct_only" : shopAvailable ? "shop_only" : "none";

/** Which Back Her Game card the hub renders for each experience. */
export const supportEntryFor = (x: SupportExperience): "chooser" | "shop" | "direct" | null =>
  x === "both" ? "chooser" : x === "shop_only" ? "shop" : x === "direct_only" ? "direct" : null;

/**
 * Commerce events (back_her_game_open, direct_support_click, shop_view) are sent
 * only for real public commerce: never in Admin Preview, never for test shops.
 */
export const shouldTrackCommerceEvents = (input: { adminPreview: boolean; shopMode: ShopMode }): boolean =>
  !input.adminPreview && input.shopMode === "live";

/** Back-compat alias used by earlier tests. */
export const resolveCommerceMode = (input: {
  adminOverride: boolean;
  settings: CommerceSettingsLite;
  athletePublished: boolean;
  liveProductCount: number;
}): ShopMode =>
  resolveShopMode({
    ...input,
    products: Array.from({ length: input.liveProductCount }, () => ({ approval_status: "live", team_ip_permission_status: "not_required" })),
  });
